import { Theme } from '@hooks/useTheme'
import { ICartItemCurrency } from '@helpers/arToysHelpers'
import { OptionsType } from 'cookies-next/lib/types'
import { deleteCookie, getCookie, hasCookie, setCookie } from 'cookies-next'
import { Referrals } from '@specs/views'
import { ExpectedProps } from '@lib/factory/serverprops'
import { ICartItemToys } from '@services/store/helpers/cart'

export namespace cookies {
    // !!! при добавлении нового ключа, обязательно нужно добавить типизацию в CookieDataMap
    export type keys =
        | 'artistsHistory'
        | 'welcomePaymentHideDate'
        | 'paymentInfo'
        | 'setsHistory'
        | 'competitionInfo'
        | 'tinderCursor'
        | 'tinderLastToyId'
        | 'tinderToyIndex'
        | 'tinderNextCursor'
        | 'redirectData'
        | 'isBannerClosed'
        | 'themeValue'
        | 'cartItems'
        | 'cartCurrencyItems'
        | 'couponInCart'
        | 'sessionid'
        | 'next-auth.session-token'
        | 'theme'
        | 'lock_blind_box_[key]'
        | '_ga'
        | 'utm'
        | 'refferal'
        | 'referralBonus'
        | 'newUser'
        | 'redirectAfterAuth'

    // !!! при добавлении нового ключа, обязательно нужно добавить типизацию в CookieDataMap

    export interface CookieDataMap extends Record<keys, any> {
        artistsHistory: number[]
        welcomePaymentHideDate: number
        paymentInfo: {
            id: number
            toyId: number
        }
        setsHistory: number[]
        competitionInfo: {
            dropId: number
            toyId: number
            competitionId: string | undefined
            competitionName: string | null | undefined
        }
        tinderCursor: number | undefined
        tinderLastToyId: number
        tinderToyIndex: number
        tinderNextCursor: number | undefined
        redirectData: IRedirectDataCookie
        isBannerClosed: boolean
        themeValue: string | null | undefined
        theme: Theme
        cartItems: ICartItemToys[]
        cartCurrencyItems: ICartItemCurrency[]
        sessionid: string
        'next-auth.session-token': string
        'lock_blind_box_[key]': { date: Date; blindBoxId: number }
        utm: {
            utm_source: string
            utm_medium: string
            utm_campaign: string
            utm_content: string
            utm_term: string
        }
        _ga: any
        refferal: string | string[]
        referralBonus: Referrals.InvitePopup
        newUser: boolean
    }

    // Переменные для редиректоа в серверсайд пропасах
    export const REDIRECT_AFTER_AUTH_COOKIE_KEY: cookies.keys = 'redirectData'
    export const COMPETITION_INFO_COOKIE_KEY: cookies.keys = 'competitionInfo'

    // ПРИМЕР:
    //    [{ key: cookies.COMPETITION_INFO_COOKIE_KEY, destination: LinksEnum.ERROR_404 }],
    //    [{ key: cookies.REDIRECT_AFTER_AUTH_COOKIE_KEY }],

    // ПРИМЕР использования фукнции get:
    // const isNewUser = cookies.get('newUser')
    export const get = <T extends keys>(cookieKey: T, options?: OptionsType): CookieDataMap[T] | undefined => {
        const cookie = getCookie(cookieKey, options)
        // return typeof cookie === 'undefined' ? undefined : typeof cookie === 'string' ? JSON.parse(cookie) : cookie

        let res
        if (typeof cookie === 'undefined') return res

        try {
            res = cookie === 'string' || typeof cookie === 'number' ? JSON.parse(cookie) : cookie
        } catch (e) {
            res = cookie
        }

        return res
    }

    // ПРИМЕР использования фукнции set:
    // cookies.set('welcomePaymentHideDate', dateExpired, { maxAge: 60 * 60 * 10 })
    export const set = <T extends keys>(cookieKey: T, data: CookieDataMap[T], options?: OptionsType) => {
        setCookie(cookieKey, data, options)
    }

    // ПРИМЕР использования фукнции remove:
    // cookies.remove('newUser')
    export const remove = (cookieKey: keys, options?: OptionsType) => {
        deleteCookie(cookieKey, options)
    }

    // ПРИМЕР использования фукнции has:
    // const newUser = cookies.has('newUser')
    export const has = (cookieKey: keys, options?: OptionsType) => hasCookie(cookieKey, options)

    // ПРИМЕР использования фукнции getFromServerSideProps в для получения кук в серверсайд пропасах:
    // const cartCurrencyItems = cookies.getFromServerSideProps(props, 'cartCurrencyItems')
    export const getFromServerSideProps = <T extends keys>(props: ExpectedProps, cookieKey: T): CookieDataMap[T] | undefined => {
        const cookie = props?.cookies?.[cookieKey]
        return typeof cookie !== 'undefined' ? JSON.parse(cookie) : undefined
    }
}

interface IRedirectDataCookie {
    url: string
    secret?: string
}
